import React from "react";
import { ArrowLeft, ArrowRight } from "../../../assets/icons";

interface PaginationProps {
    totalPages: number;
    currentPage: number;
    displayRange?: number;
    onPageChange: (page: number) => void;
}

const Pagination: React.FC<PaginationProps> = ({
    totalPages,
    currentPage,
    onPageChange,
    displayRange = 4,
}) => {
    const startPage = Math.max(1, currentPage - Math.floor(displayRange / 2));
    const endPage = Math.min(totalPages, startPage + displayRange - 1);

    const pageNumbers = Array.from(
        { length: endPage - startPage + 1 },
        (_, i) => startPage + i
    );

    return (
        <div className="flex flex-col items-center mt-4 space-y-4 md:space-y-0 md:flex-row md:justify-between">
            {/* Desktop Layout: Previous Button, Pagination, Next Button */}
            <div className="hidden md:flex md:items-center md:justify-between w-full">
                <button
                    className={`flex items-center justify-center rounded-[8px] border-[1px] text-neutral-25 border-neutral-25 py-[8px] px-[14px] text-[14px] ${currentPage === 1
                            ? "bg-darkcolor-300 cursor-not-allowed"
                            : "bg-transparent"
                        }`}
                    style={{ border: "1px solid" }}
                    onClick={() => onPageChange(currentPage - 1)}
                    disabled={currentPage === 1}
                >
                    <ArrowLeft size={20} />
                    Previous
                </button>

                <div className="flex items-center justify-center space-x-2 mx-4">
                    {startPage > 1 && (
                        <>
                            {startPage > 2 && (
                                <button className="w-[40px] h-[40px] text-neutral-25">
                                    ...
                                </button>
                            )}
                            <button
                                className="w-[40px] h-[40px] text-neutral-25"
                                onClick={() => onPageChange(1)}
                            >
                                {1}
                            </button>
                        </>
                    )}
                    {pageNumbers.map((number) => (
                        <button
                            key={number}
                            className={`w-[40px] h-[40px] text-neutral-25 ${currentPage === number ? "rounded-full bg-darkcolor-500" : ""
                                }`}
                            onClick={() => onPageChange(number)}
                        >
                            {number}
                        </button>
                    ))}
                    {endPage < totalPages && (
                        <>
                            {endPage < totalPages - 1 && (
                                <button className="w-[40px] h-[40px] text-neutral-25">
                                    ...
                                </button>
                            )}
                            <button
                                className="w-[40px] h-[40px] text-neutral-25"
                                onClick={() => onPageChange(totalPages)}
                            >
                                {totalPages}
                            </button>
                        </>
                    )}
                </div>

                <button
                    className={`flex items-center justify-center rounded-[8px] border-neutral-25 py-[8px] px-[14px] text-[14px] border-[1px] text-neutral-25 ${currentPage === totalPages
                            ? "bg-darkcolor-300 cursor-not-allowed"
                            : "bg-transparent"
                        }`}
                    style={{ border: "1px solid" }}
                    onClick={() => onPageChange(currentPage + 1)}
                    disabled={currentPage === totalPages}
                >
                    Next
                    <ArrowRight size={20} />
                </button>
            </div>

            {/* Mobile Layout: Separate Rows for Pagination and Buttons */}
            <div className="flex flex-col w-full md:hidden">
                <div className="flex items-center justify-center space-x-2 mb-2">
                    {startPage > 1 && (
                        <>
                            {startPage > 2 && (
                                <button className="w-[40px] h-[40px] text-neutral-25">
                                    ...
                                </button>
                            )}
                            <button
                                className="w-[40px] h-[40px] text-neutral-25"
                                onClick={() => onPageChange(1)}
                            >
                                {1}
                            </button>
                        </>
                    )}
                    {pageNumbers.map((number) => (
                        <button
                            key={number}
                            className={`w-[40px] h-[40px] text-neutral-25 ${currentPage === number ? "rounded-full bg-darkcolor-500" : ""
                                }`}
                            onClick={() => onPageChange(number)}
                        >
                            {number}
                        </button>
                    ))}
                    {endPage < totalPages && (
                        <>
                            {endPage < totalPages - 1 && (
                                <button className="w-[40px] h-[40px] text-neutral-25">
                                    ...
                                </button>
                            )}
                            <button
                                className="w-[40px] h-[40px] text-neutral-25"
                                onClick={() => onPageChange(totalPages)}
                            >
                                {totalPages}
                            </button>
                        </>
                    )}
                </div>
                <div className="flex justify-between w-full px-4">
                    <button
                        className={`flex items-center justify-center rounded-[8px] border-[1px] text-neutral-25 border-neutral-25 py-[8px] px-[14px] text-[14px] ${currentPage === 1
                                ? "bg-darkcolor-300 cursor-not-allowed"
                                : "bg-transparent"
                            }`}
                        style={{ border: "1px solid" }}
                        onClick={() => onPageChange(currentPage - 1)}
                        disabled={currentPage === 1}
                    >
                        <ArrowLeft size={20} />
                        Previous
                    </button>
                    <button
                        className={`flex items-center justify-center rounded-[8px] border-neutral-25 py-[8px] px-[14px] text-[14px] border-[1px] text-neutral-25 ${currentPage === totalPages
                                ? "bg-darkcolor-300 cursor-not-allowed"
                                : "bg-transparent"
                            }`}
                        style={{ border: "1px solid" }}
                        onClick={() => onPageChange(currentPage + 1)}
                        disabled={currentPage === totalPages}
                    >
                        Next
                        <ArrowRight size={20} />
                    </button>
                </div>
            </div>
        </div>
    );
};

export default React.memo(Pagination);
